'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';

export default function MessagingGuidePage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Sending Your First Message</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          
          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#overview" className="block text-sm text-gray-600 hover:text-brand transition-colors">Overview</a>
                <a href="#sending" className="block text-sm text-gray-600 hover:text-brand transition-colors">Sending Messages</a>
                <a href="#receiving" className="block text-sm text-gray-600 hover:text-brand transition-colors">Receiving Messages</a>
                <a href="#attachments" className="block text-sm text-gray-600 hover:text-brand transition-colors">Attachments</a>
                <a href="#credits" className="block text-sm text-gray-600 hover:text-brand transition-colors">Understanding Credits</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Sending Your First Message</h1>
              <p className="text-xl text-gray-600">
                Learn how to send and receive messages with your clients through Voley, and understand how credits work.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Estimated time: 5 minutes</span>
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">
              
              <section id="overview" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Overview</h2>
                <p className="text-gray-600 mb-4">
                  Once your phone number is approved, you can start texting clients directly from Voley. Messages are sent as SMS (text) or MMS (with attachments), and clients can reply directly to the conversation.
                </p>
                <p className="text-gray-600 mb-6">
                  All conversations are stored in Voley and can be logged to your AMS if you have an integration set up.
                </p>
              </section>

              <section id="sending" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Sending Messages</h2>
                
                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">Starting a New Conversation</h3>
                <p className="text-gray-600 mb-4">
                  To send a message to a client:
                </p>
                <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Click the <strong>compose</strong> button (pencil icon) in the top left of the messaging view</li>
                  <li>Search for a contact by name or phone number, or enter a new phone number</li>
                  <li>Type your message in the text box at the bottom</li>
                  <li>Press <strong>Enter</strong> or click the send button</li>
                </ol>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">Replying to Existing Conversations</h3>
                <p className="text-gray-600 mb-4">
                  Your conversation list shows all active client conversations. Click any conversation to open it, type your reply, and send. The most recent conversations appear at the top.
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">Sending to Multiple Contacts (Bulk Messaging)</h3>
                <p className="text-gray-600 mb-4">
                  Need to send the same message to multiple clients? Voley makes it easy to reach groups of clients at once. Each recipient receives the message as their own individual conversation — it&apos;s not a group chat, so replies come back privately.
                </p>
                
                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">How to Send a Bulk Message</h4>
                <ol className="list-decimal pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Go to your <strong>Contacts</strong> page</li>
                  <li>Use <strong>filters</strong> or <strong>tags</strong> to narrow down to the clients you want to reach</li>
                  <li>Click <strong>Select All</strong> (or manually select specific contacts)</li>
                  <li>Click the <strong>Send Message</strong> button in the bulk actions bar</li>
                  <li>Write your message and send</li>
                </ol>

                <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-900">Ideas for Bulk Messaging</h4>
                <p className="text-gray-600 mb-4">
                  Most agencies haven&apos;t had an easy way to text clients in bulk before. Here are some ways to use this feature:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Holiday greetings:</strong> Send a quick &quot;Happy Holidays from [Your Agency]!&quot; to all clients</li>
                  <li><strong>Renewal reminders:</strong> Filter by clients with upcoming renewals and send a friendly heads-up</li>
                  <li><strong>Weather alerts:</strong> Tag clients in affected areas and send storm prep reminders or claims info</li>
                  <li><strong>Policy updates:</strong> Notify specific client groups about carrier changes or new coverage options</li>
                  <li><strong>Office announcements:</strong> Let clients know about holiday hours, new team members, or office moves</li>
                  <li><strong>Thank you messages:</strong> Reach out to new clients or those who referred someone</li>
                </ul>

                <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Pro tip:</strong> Use tags to organize your contacts (e.g., &quot;Commercial&quot;, &quot;Personal Lines&quot;, &quot;VIP&quot;, &quot;Flood Zone&quot;). Then you can quickly filter and message specific groups whenever needed.
                  </p>
                </div>

                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Tip:</strong> Messages are delivered instantly. You&apos;ll see a delivery status indicator showing when your message has been sent.
                  </p>
                </div>
              </section>

              <section id="receiving" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Receiving Messages</h2>
                <p className="text-gray-600 mb-4">
                  When a client replies to your message, their response appears in the conversation in real-time. You&apos;ll also receive:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>In-app notifications:</strong> A badge appears on the notification bell showing unread messages</li>
                  <li><strong>Browser notifications:</strong> If you&apos;ve granted permission, you&apos;ll see a desktop notification when new messages arrive (even when Voley is in a background tab)</li>
                  <li><strong>Unread indicators:</strong> Conversations with unread messages are highlighted in your conversation list</li>
                </ul>
              </section>

              <section id="attachments" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Sending Attachments</h2>
                <p className="text-gray-600 mb-4">
                  You can attach photos and files to your messages. Click the attachment icon (paperclip) in the message input area to add files.
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Max file size:</strong> 10MB per attachment</li>
                  <li><strong>Supported formats:</strong> Images (JPG, PNG, GIF), PDFs, and common document types</li>
                </ul>
                <div className="bg-brand-yellow/15 border border-brand-yellow/40 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Note:</strong> Adding any attachment converts your message from SMS to MMS, which uses 3 credits instead of 1.
                  </p>
                </div>
              </section>

              <section id="credits" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Understanding Credits</h2>
                <p className="text-gray-600 mb-4">
                  Voley uses a credit system for outgoing messages:
                </p>
                
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">1</p>
                      <p className="text-sm text-gray-600">credit per SMS</p>
                      <p className="text-xs text-gray-500 mt-1">Text-only messages</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <p className="text-3xl font-bold text-gray-900">3</p>
                      <p className="text-sm text-gray-600">credits per MMS</p>
                      <p className="text-xs text-gray-500 mt-1">Messages with attachments</p>
                    </div>
                  </div>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">Your Monthly Credits</h3>
                <p className="text-gray-600 mb-4">
                  The Core plan includes <strong>1,000 credits per month</strong> with the base price. Each additional user on your account adds another <strong>200 credits per month</strong>.
                </p>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">Going Over Your Limit</h3>
                <p className="text-gray-600 mb-4">
                  You&apos;re never cut off from messaging. If you use all your monthly credits, additional messages are charged at <strong>3¢ per credit</strong>. These overage charges are added to your next invoice.
                </p>

                <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl p-5 mb-6">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>Example:</strong> If you send 50 additional SMS messages after using your monthly credits, that&apos;s 50 credits × $0.03 = $1.50 in overage charges.
                  </p>
                </div>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">What Uses Credits</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Outgoing messages:</strong> Messages you send to clients</li>
                  <li><strong>Incoming messages:</strong> Client replies to your messages</li>
                </ul>

                <h3 className="text-xl font-semibold mt-8 mb-4 text-gray-900">What Doesn&apos;t Use Credits</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Team chat:</strong> Internal messages between your team members</li>
                  <li><strong>Internal notes:</strong> Notes added to conversations</li>
                </ul>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link 
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  Need help? <Link href="/contact" className="text-brand hover:text-[#2a6bb0] font-medium">Contact our team</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
